import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  HeartIcon,
  XMarkIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import MessageDisplay from './MessageDisplay';

/**
 * QuickReorderManager Component
 * 
 * PURPOSE: Save current cart items as a quick reorder for future use
 * ENDPOINT: POST /api/v1/advanced-orders/quick-reorder/create
 * 
 * This component is used in the cart page to allow users to save their 
 * current cart items as a quick reorder without placing an order first.
 * 
 * For saving existing/completed orders as quick reorders, use the 
 * SaveOrderAsQuickReorder component instead.
 */

const API_BASE = 'https://mongobyte-hc73.onrender.com/api/v1';

const REORDER_API = {
  baseUrl: API_BASE,
  endpoints: {
    saveExistingOrder: '/advanced-orders/quick-reorder',        // Save existing order as quick reorder
    createQuickReorder: '/advanced-orders/quick-reorder/create', // Create quick reorder directly from meals
    getQuickReorders: '/advanced-orders/quick-reorders',
    executeQuickReorder: (id) => `/advanced-orders/quick-reorder/${id}/execute`,
    regularReorder: '/orders/by-reference'
  }
};

const QuickReorderManager = ({ userToken, cartItems, restaurant, onSaveSuccess }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderName, setOrderName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const showMessage = useCallback((type, title, content, details = null) => {
    setMessage({ type, title, content, details });
    setTimeout(() => setMessage(null), 5000);
  }, []);

  // Save cart items as quick reorder - using the "create directly" endpoint
  const saveAsQuickReorder = async () => {
    if (!orderName.trim()) {
      showMessage('warning', 'Name Required', 'Please enter a name for your quick reorder');
      return;
    }

    if (!cartItems || cartItems.length === 0) {
      showMessage('warning', 'Empty Cart', 'Your cart is empty. Add some items first.');
      return;
    }

    setIsSaving(true);
    try {
      // Calculate total amount from cart items
      const totalAmount = cartItems.reduce((total, item) => 
        total + (item.meal.price * item.quantity), 0
      );

      // Prepare meals data
      const meals = cartItems.map(item => ({
        meal: item.meal.customId || item.meal._id || item.meal,
        quantity: item.quantity
      }));

      // Use the "create quick reorder directly" endpoint
      const response = await fetch(`${REORDER_API.baseUrl}${REORDER_API.endpoints.createQuickReorder}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({
          name: orderName.trim(),
          meals: meals,
          restaurant: restaurant._id || restaurant,
          totalAmount: totalAmount
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || `HTTP ${response.status}: Failed to create quick reorder`);
      }
      
      if (result.success) {
        showMessage('success', 'Quick Reorder Created!', `"${orderName}" saved as quick reorder!`);
        setOrderName('');
        setIsModalOpen(false);
        if (onSaveSuccess) {
          onSaveSuccess(result.data);
        }
      } else {
        throw new Error(result.message || 'Failed to create quick reorder');
      }
    } catch (error) {
      console.error('Error creating quick reorder:', error);
      showMessage('error', 'Save Failed', error.message || 'Failed to create quick reorder');
    } finally {
      setIsSaving(false);
    }
  };

  const totalAmount = cartItems?.reduce((total, item) => 
    total + (item.meal.price * item.quantity), 0
  ) || 0;

  const canSaveQuickReorder = cartItems && cartItems.length > 0 && restaurant;

  return (
    <>
      {/* Save as Quick Reorder Button */}
      <button
        onClick={() => setIsModalOpen(true)}
        disabled={!canSaveQuickReorder}
        className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
          canSaveQuickReorder
            ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        title={!canSaveQuickReorder ? 'Add items to cart first' : 'Save this order for quick reordering'}
      >
        <div className="flex items-center justify-center gap-2">
          <HeartIcon className="h-5 w-5" />
          <span>Save as Quick Reorder</span>
        </div>
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <HeartSolid className="h-6 w-6 text-red-500" />
                <h3 className="text-xl font-bold text-gray-900">Save Quick Reorder</h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order Name
                </label>
                <input
                  type="text"
                  value={orderName}
                  onChange={(e) => setOrderName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                  placeholder="e.g., My Usual Breakfast, Friday Lunch"
                  maxLength={50}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Give this order a memorable name for easy reordering
                </p>
              </div>

              {/* Order Preview */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Order Summary</h4>
                <div className="space-y-2">
                  {cartItems?.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>{item.quantity}x {item.meal.name}</span>
                      <span>₦{(item.meal.price * item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-2 mt-3">
                  <div className="flex justify-between font-medium">
                    <span>Total</span>
                    <span>₦{totalAmount.toLocaleString()}</span>
                  </div>
                </div>
                <div className="text-sm text-gray-600 mt-2">
                  Restaurant: {restaurant?.name}
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="font-medium text-blue-900 mb-2">Quick Reorder Benefits:</h5>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Instantly reorder your favorite meals</li>
                  <li>• No need to browse the menu again</li>
                  <li>• Update delivery details if needed</li>
                  <li>• Perfect for regular orders</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAsQuickReorder}
                  disabled={isSaving || !orderName.trim()}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                    isSaving || !orderName.trim()
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                >
                  {isSaving ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      <span>Saving...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <PlusIcon className="h-4 w-4" />
                      <span>Save Quick Reorder</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Message Display */}
      <MessageDisplay message={message} onClose={() => setMessage(null)} />
    </>
  );
};

export default QuickReorderManager;
